import { useRef, useState } from 'react'
import { type LoaderFunction, MetaFunction, json } from '@remix-run/node'
import { useLoaderData, useNavigate, useOutletContext } from '@remix-run/react'
import type { CountryCode } from 'libphonenumber-js'
import { ValidatedForm } from 'remix-validated-form'
import { getCountries } from 'react-phone-number-input'

import { Button, WithUseField, PhoneInputField } from '~/components/shared'
import TextInput from '~/components/input-types/TextInput_v2'
import ComboboxInput from '~/components/input-types/ComboboxInput_v2'
import SelectInput from '~/components/input-types/SelectInput_v2'
import PhoneInput from '~/components/input-types/PhoneInput_v2'
import CheckboxInput from '~/components/input-types/CheckboxInput'
import { type AffiliateOutletData, ROUTES } from '~/components/affiliate/Affiliate.config'
import Validator from '~/helpers/validation'
import { usePhoneValues } from '~/components/affiliate/usePhoneValues'
import { createParentAffiliateIdCookie, getParentAffiliateIdCookie } from '~/cookies'
import PasswordInput from '~/components/input-types/PasswordInput'
import { buildTabTitle } from '~/helpers/string'
import { InformationIcon } from '~/components/icons/InformationIcon'

type LoaderData = {
  tabTitle: string
}

export const loader: LoaderFunction = async ({ request }) => {
  let headers = new Headers()

  const url = new URL(request.url)

  // CX Parent Affiliate ID cookie
  if (url.searchParams.get('bta') && getParentAffiliateIdCookie(request.headers) === null) {
    const affiliateParentIdCookie = createParentAffiliateIdCookie(url, request.headers)
    if (affiliateParentIdCookie) headers.append('Set-Cookie', affiliateParentIdCookie)
  }

  return json<LoaderData>({ tabTitle: buildTabTitle('Affiliate Sign Up') }, { headers: headers })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [{ title: data.tabTitle }]
}

export default function AffiliatePersonalInfoPage() {
  const {} = useLoaderData<LoaderData>()
  const navigate = useNavigate()
  const { mainDomainUrl, countryCode, countries, communicationLanguages, personalInfo, setPersonalInfo } =
    useOutletContext<AffiliateOutletData>()
  const formRef = useRef<HTMLFormElement>(null)

  const [defaultCountry, setDefaultCountry] = useState<CountryCode | undefined>(personalInfo.countryCode ?? countryCode)

  const phoneValues = usePhoneValues({
    initialPhoneNumber: personalInfo.phoneNumber,
    initialWhatsappNumber: personalInfo.whatsappNumber,
    initialWhatsappSameAsPhone: Boolean(personalInfo.whatsappSameAsPhone),
  })

  const phoneNumberHelper = 'Your phone number will be used for 2FA to secure your account.'
  const singleRegAllowedMsg =
    'Please note: only one registration per affiliate is allowed. Multiple registrations may result in the termination of services.'

  return (
    <div className="flex w-full flex-col gap-12 md:flex-row md:gap-4 lg:gap-12 xl:gap-24">
      <div className="flex-1">
        <h1 className="text-balance text-3xl font-semibold text-black sm:text-5xl">Affiliate Sign Up</h1>
        <p className="mt-6 text-balance text-lg font-normal text-brand-violet-950 sm:text-2xl">
          Get your custom referral link, and start earning rewards
        </p>
        <img
          src="/images/affiliate/affiliate-sign-up-cover.png"
          alt="Affiliate Sign Up Cover"
          className="mt-10 h-auto w-full object-contain"
        />
      </div>
      <div className="flex w-full flex-col gap-8 md:max-w-md">
        <ValidatedForm
          formRef={formRef}
          validator={Validator.affiliatePersonalInfoStep()}
          onSubmit={(data, event) => {
            event.preventDefault()
            setPersonalInfo((prev) => ({
              ...prev,
              ...data,
              whatsappSameAsPhone: phoneValues.whatsappSameAsPhone,
            }))
            navigate(ROUTES.businessDetails)
          }}
          className="flex flex-col gap-6"
        >
          <WithUseField name="firstName">
            {({ error }) => (
              <TextInput
                id="firstName"
                name="firstName"
                label="First Name"
                value={personalInfo.firstName}
                helperText={error || 'Enter your first name as it appears on official documents.'}
              />
            )}
          </WithUseField>
          <WithUseField name="lastName">
            {({ error }) => (
              <TextInput
                id="lastName"
                name="lastName"
                label="Last Name"
                value={personalInfo.lastName}
                helperText={error || 'Enter your last name as it appears on official documents.'}
              />
            )}
          </WithUseField>
          <TextInput id="email" name="email" label="Email" value={personalInfo.email} />
          <ComboboxInput
            id="countryCode"
            name="countryCode"
            label="Country of Residence"
            values={countries}
            defaultKey={personalInfo.countryCode ?? countryCode}
            onChange={(v) => getCountries().includes(v as CountryCode) && setDefaultCountry(v as CountryCode)}
          />
          <SelectInput
            id="languageId"
            name="languageId"
            label="Communication Language"
            defaultValue={personalInfo.languageId}
            renderSelected={(selected) =>
              communicationLanguages.find((item) => `${item.id}` === `${selected}`)?.displayName ?? ''
            }
          >
            {communicationLanguages.map((item) => (
              <SelectInput.Item key={item.id} value={item.id}>
                {item.displayName}
              </SelectInput.Item>
            ))}
          </SelectInput>
          <PhoneInput
            id="phoneNumber"
            name="phoneNumber"
            label="Phone"
            defaultCountry={defaultCountry as CountryCode}
            value={phoneValues.phone}
            onChange={phoneValues.onPhoneChange}
            helperText={<span className="text-brand-actions-progress">{phoneNumberHelper}</span>}
          />
          <div className="flex flex-col gap-2.5">
            <WithUseField name="whatsappNumber">
              {({ error }) => (
                <>
                  <PhoneInputField
                    label="WhatsApp"
                    optional
                    disabled={phoneValues.whatsappSameAsPhone}
                    defaultCountry={defaultCountry as CountryCode}
                    value={phoneValues.whatsapp}
                    onChange={phoneValues.onWhatsappChange}
                    helperText={error}
                    error={Boolean(error)}
                  />
                  <input type="hidden" name="whatsappNumber" value={phoneValues.whatsapp} />
                </>
              )}
            </WithUseField>
            <CheckboxInput
              id="whatsappSameAsPhone"
              name="whatsappSameAsPhone"
              label="Same as my current phone number"
              optional
              checked={personalInfo.whatsappSameAsPhone}
              onChange={phoneValues.onWhatsappSameAsPhoneCheckboxChange}
            />
          </div>
          <PasswordInput
            id="password"
            name="password"
            label="Password"
            defaultValue={personalInfo.password}
            helperText={'Password must be at least 6 characters and include a number.'}
          />
          <PasswordInput
            id="passwordConfirmation"
            name="passwordConfirmation"
            label="Confirm password"
            defaultValue={personalInfo.passwordConfirmation}
          />

          <CheckboxInput
            id="tcAcknowledgement"
            name="tcAcknowledgement"
            label={`I have read and agree to the <a target="_blank" href="${mainDomainUrl}/terms-conditions">Terms & Conditions</a> and <a target="_blank" href="${mainDomainUrl}/privacy-policy">Privacy Policy</a>`}
            checked={personalInfo.tcAcknowledgement}
            onChange={(value) => {
              setPersonalInfo((prev) => ({
                ...prev,
                tcAcknowledgement: value,
              }))
            }}
          />

          <div className="flex items-start gap-2.5">
            <InformationIcon className="flex-shrink-0" />
            <span className="text-sm font-normal text-brand-actions-progress">{singleRegAllowedMsg}</span>
          </div>
        </ValidatedForm>
        <div className="mb-10 flex flex-col gap-2.5">
          <Button
            className="mx-auto w-full max-w-md sm:ml-auto"
            onClick={() =>
              formRef.current?.dispatchEvent(
                new Event('submit', {
                  cancelable: true,
                  bubbles: true,
                }),
              )
            }
          >
            Next
          </Button>
          <p className="text-center text-sm">
            Already have an account?&nbsp;
            <a href="https://affiliate.thetradingpit.com/login/" className="font-semibold">
              Log In now
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
